import type { DraftFunction } from 'use-immer';
import type { FormValues } from '@linktivity/link-ui';
import {
  CurrencyCode,
  DiscrepancyStatus,
  DiscrepancyType,
  DiscrepancyWaitingStatus
} from '../services/const';
import { Discrepancy, TotalPriceForBooking } from '../services/types';
import { filterEmptyParams } from './filter';

export function isFunction<T>(
  updater: T | DraftFunction<T>
): updater is DraftFunction<T> {
  return typeof updater === 'function';
}

export function isURL(url: string) {
  return url.startsWith('http');
}

const BULK_BOOKING_ID_FLAG = 'BULK';

// 团体预约（BULK-xxx）跳转团体订单详情，常规预约跳转预约详情
export function getBookingDetailPath(bookingCustomId?: string) {
  if (!bookingCustomId) {
    return '';
  }
  return bookingCustomId.toUpperCase().includes(BULK_BOOKING_ID_FLAG)
    ? `/bulk-order/list/${bookingCustomId}`
    : `/book/booking/${bookingCustomId}`;
}

export const toCamelCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

export const formatPrice = (price?: string | number, currency?: string) => {
  if (price === undefined) return '';
  const formatted = price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return currency && currency !== 'UNDEFINED'
    ? `${currency} ${formatted}`
    : formatted;
};

export const formatPercentage = (
  value?: string | number,
  accuracy?: number
) => {
  if (value === undefined || value === null || value === '') return '';
  const percentage = Number(value) * 100;
  // 传入精度时按该位数固定展示（0 → 87、2 → 87.14、4 → 87.1429）
  if (accuracy !== undefined && accuracy !== null) {
    return `${percentage.toFixed(accuracy)}%`;
  }
  const rounded = Math.round((percentage + Number.EPSILON) * 100) / 100;
  return `${rounded}%`;
};

export interface IHandlePriceOptions {
  format?: boolean;
  currencyCode?: CurrencyCode;
  plusSign?: boolean;
}
export const handlePrice = (
  price: string | number = 0,
  { format = true, currencyCode, plusSign = false }: IHandlePriceOptions = {}
) => {
  const isMinus = Number(price) < 0;
  let targetPrice: string | number = Math.abs(Number(price));
  if (format) {
    targetPrice = formatPrice(targetPrice);
  }
  if (currencyCode) {
    targetPrice = ` ${currencyCode} ${targetPrice}`;
  }
  if (isMinus) {
    targetPrice = `-${targetPrice}`;
  } else if (plusSign) {
    targetPrice = `+${targetPrice}`;
  }
  return targetPrice;
};

export function getAdjustedPrices(
  discrepancies: Discrepancy[] = [],
  originalPrices?: TotalPriceForBooking,
  isAgent = false
) {
  const { grossPrice, netPrice } = discrepancies.reduce(
    (acc, curr) => {
      const exchangePrice = isAgent
        ? curr.otaExchangePrice
        : curr.supplierExchangePrice;
      const grossAdjustment = exchangePrice?.grossAdjustmentAmount || 0;
      const netAdjustment = exchangePrice?.netAdjustmentAmount || 0;

      if (curr.discrepancyType === DiscrepancyType.Refund) {
        return {
          grossPrice: acc.grossPrice - grossAdjustment,
          netPrice: acc.netPrice - netAdjustment
        };
      } else {
        return {
          grossPrice: acc.grossPrice + grossAdjustment,
          netPrice: acc.netPrice + netAdjustment
        };
      }
    },
    {
      grossPrice:
        originalPrices?.grossFinalPrice ?? originalPrices?.grossPrice ?? 0,
      netPrice: originalPrices?.netFinalPrice ?? originalPrices?.netPrice ?? 0
    }
  );
  return {
    grossPrice,
    netPrice
  };
}

//勾选「送信」→ discrepancy_status 加 WAITING, discrepancy_waiting_status 传 DELIVER
//勾选「受信」→ discrepancy_status 加 WAITING,discrepancy_waiting_status 传 RECEIVE
export const buildDiscrepancyStatusParams = (formValues: FormValues) => {
  const selected = (formValues.discrepancyStatus as string[]) ?? [];
  const waitingStatusValues = Object.values(
    DiscrepancyWaitingStatus
  ) as string[];
  const discrepancyWaitingStatus = selected.filter(v =>
    waitingStatusValues.includes(v)
  );
  const discrepancyStatus = [
    ...(discrepancyWaitingStatus.length ? [DiscrepancyStatus.Waiting] : []),
    ...selected.filter(v => !waitingStatusValues.includes(v))
  ];

  return filterEmptyParams<FormValues>({
    ...formValues,
    discrepancyStatus,
    discrepancyWaitingStatus
  });
};

export function getDiscrepancyPricePrefix(
  currency?: string,
  type?: DiscrepancyType
) {
  if (type === DiscrepancyType.Refund) {
    return `- ${currency} `;
  }
  if (type === DiscrepancyType.AdditionalCharge) {
    return `+ ${currency} `;
  }
  return '';
}

interface OriginRegion {
  country?: { text?: string } | null;
  administrativeAreaLevel1?: { text?: string } | null;
}

const getOriginRegionKey = (item: OriginRegion) =>
  `${item.country?.text ?? ''}|${item.administrativeAreaLevel1?.text ?? ''}`;

export function uniqueOriginsByRegion<T extends OriginRegion>(
  origins?: T[] | null
): T[] | undefined {
  return origins?.filter(
    (item, index, arr) =>
      arr.findIndex(
        origin => getOriginRegionKey(origin) === getOriginRegionKey(item)
      ) === index
  );
}
